import { AbstractShape } from '../abstract/Shape';
import { Connection } from '../clases/Connection';
import { Shape } from '../enums/Shape';
import { ArrowStyle, ArrowType } from '../enums/ArrowType';
import { Anchor } from '../enums/Anchor';
import { DBItem } from '../interfaces/Record';
import { LiveCanvasOperation } from '../interfaces/live-creator.contract';
import { GetThemeByName } from '../styles/Canvas';
import { createCanvasItem } from '../utils/ContainerUtils';
import { flattenShapes } from './live-canvas-snapshot';
import { isRecord } from './services/agent-stream';

export interface PatchOwner {
  canvasItems: AbstractShape[];
  connections: Connection[];
}
export interface PatchSemantics { nodes: Map<string, DBItem>; relations: Map<string, DBItem> }
const fields: Record<string, string[]> = {
  NODE_ADD: ['dbId', 'parentDbId', 'x', 'y', 'width', 'height', 'shape', 'color'],
  NODE_REMOVE_SUBTREE: ['dbId'], NODE_REPARENT: ['dbId', 'newParentDbId', 'x', 'y'],
  NODE_GEOMETRY_SET: ['dbId', 'x', 'y', 'width', 'height'],
  NODE_STYLE_SET: ['dbId', 'color', 'textWidth', 'dottedSpacing'],
  NODE_SHAPE_SET: ['dbId', 'shape', 'x', 'y', 'width', 'height'], NODE_REFRESH_SEMANTICS: ['dbId'],
  CONNECTION_ADD: ['relation', 'sourceDbId', 'targetDbId', 'lineColor', 'arrowStyle', 'arrowType', 'anchorFrom', 'anchorTo'],
  CONNECTION_REMOVE: ['relation'],
  CONNECTION_STYLE_SET: ['relation', 'lineColor', 'arrowStyle', 'arrowType', 'anchorFrom', 'anchorTo', 'textPositionPercent', 'lArrowHorizontalSpacing', 'lArrowVerticalSpacing']
};

export function parseOperations(value: unknown): LiveCanvasOperation[] {
  if (!Array.isArray(value) || value.length > 2000) throw new Error('Invalid Canvas operations');
  for (const op of value) {
    if (!isRecord(op) || typeof op['type'] !== 'string' || !Object.prototype.hasOwnProperty.call(fields, op['type'])) throw new Error('Unknown Canvas operation');
    const allowed = fields[op['type']];
    const unknownField = Object.keys(op).find(key => key !== 'type' && !allowed.includes(key));
    if (unknownField) {
      const safeField = /^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(unknownField) ? unknownField : 'invalid-key';
      throw new Error(`Unknown operation field: ${op['type']}.${safeField}`);
    }
    for (const key of allowed) {
      const value = op[key];
      if (value === undefined) continue;
      if (['x', 'y', 'width', 'height', 'textWidth', 'dottedSpacing', 'textPositionPercent', 'lArrowHorizontalSpacing', 'lArrowVerticalSpacing'].includes(key)) {
        if (typeof value !== 'number' || !Number.isFinite(value) || (['width', 'height'].includes(key) && value <= 0)) throw new Error('Invalid geometry');
      } else if (typeof value !== 'string' || !value) throw new Error('Invalid identity/style');
    }
    const required = op['type'] === 'NODE_ADD' ? ['dbId', 'parentDbId', 'x', 'y', 'width', 'height'] :
      op['type'] === 'NODE_REPARENT' ? ['dbId', 'newParentDbId', 'x', 'y'] :
      op['type'] === 'NODE_SHAPE_SET' ? ['dbId', 'shape'] :
      op['type'] === 'CONNECTION_ADD' ? ['relation', 'sourceDbId', 'targetDbId'] :
      op['type'].startsWith('NODE_') ? ['dbId'] : ['relation'];
    if (required.some(key => op[key] === undefined)) throw new Error('Missing operation field');
    if (op['shape'] !== undefined && !Object.values(Shape).includes(op['shape'] as Shape)) throw new Error('Unknown shape');
    if (op['color'] !== undefined && !GetThemeByName(op['color'] as string)) throw new Error('Unknown color');
    for (const [key, choices] of [['arrowStyle', Object.values(ArrowStyle)], ['arrowType', Object.values(ArrowType)],
      ['anchorFrom', Object.values(Anchor)], ['anchorTo', Object.values(Anchor)]] as const) {
      if (op[key] !== undefined && !(choices as readonly string[]).includes(op[key] as string)) throw new Error('Unknown connection style');
    }
  }
  return structuredClone(value) as LiveCanvasOperation[];
}

/** Synchronous transaction on existing instances. No database mutations, redraw or Save. */
export function applyCanvasPatch(owner: PatchOwner, targetRoots: readonly AbstractShape[], operations: LiveCanvasOperation[], semantics: PatchSemantics, render?: () => void): void {
  const all = flattenShapes(owner.canvasItems);
  const targets = flattenShapes(targetRoots);
  const nodes = new Map<string, AbstractShape>();
  for (const item of targets) {
    const id = item.dbPropierties?.__EDT_UUID__;
    if (!id) continue;
    if (nodes.has(id)) throw new Error('More than one Canvas instance represents this node');
    nodes.set(id, item);
  }
  const scopedIds = new Set(targets.map(item => item.id));
  const relations = new Map(owner.connections.filter(item => scopedIds.has(item.iFrom.id) && item.iTo && scopedIds.has(item.iTo.id))
    .map(item => [item.dbPropierties?.__EDT_UUID__ ?? item.id, item]));
  const objects = [...all, ...owner.connections];
  const undo = objects.map(object => [object, Object.fromEntries(Object.entries(Object.getOwnPropertyDescriptors(object)).map(([key, descriptor]) =>
    [key, 'value' in descriptor && Array.isArray(descriptor.value) ? { ...descriptor, value: [...descriptor.value] } : descriptor]))] as const);
  const oldRoots = [...owner.canvasItems];
  const oldConnections = [...owner.connections];
  const node = (id: string) => { const result = nodes.get(id); if (!result) throw new Error('Node outside captured Model'); return result; };
  const detach = (item: AbstractShape) => {
    if (item.parent) item.parent.children = item.parent.children.filter(child => child !== item);
    owner.canvasItems = owner.canvasItems.filter(root => root !== item);
    item.setParent(undefined);
  };
  const place = (item: AbstractShape, parent: AbstractShape) => {
    for (let ancestor: AbstractShape | undefined = parent; ancestor; ancestor = ancestor.parent) if (ancestor === item) throw new Error('Cyclic parent');
    detach(item);
    parent.addChild(item);
  };
  try {
    for (const op of operations) {
      if (op.type === 'NODE_ADD') {
        if (nodes.has(op.dbId)) throw new Error('Node already visible');
        const data = semantics.nodes.get(op.dbId);
        if (!data) throw new Error('Authoritative node data missing');
        const item = createCanvasItem(data);
        nodes.set(op.dbId, item);
        place(item, node(op.parentDbId));
        if (op.shape) item.setShape(op.shape as Shape);
        if (op.color) item.setTheme(GetThemeByName(op.color));
        item.setSize(op.width, op.height);
        item.x = op.x; item.y = op.y;
      } else if (op.type === 'NODE_REMOVE_SUBTREE') {
        const item = node(op.dbId);
        const removed = new Set(flattenShapes([item]));
        detach(item);
        owner.canvasItems = owner.canvasItems.filter(root => !removed.has(root));
        owner.connections = owner.connections.filter(connection => !removed.has(connection.iFrom) && (!connection.iTo || !removed.has(connection.iTo)));
        for (const [id, value] of nodes) if (removed.has(value)) nodes.delete(id);
        for (const [id, value] of relations) if (!owner.connections.includes(value)) relations.delete(id);
      } else if (op.type === 'NODE_REPARENT') {
        const item = node(op.dbId); place(item, node(op.newParentDbId)); item.x = op.x; item.y = op.y;
      } else if (op.type === 'NODE_REFRESH_SEMANTICS') {
        const item = node(op.dbId), data = semantics.nodes.get(op.dbId);
        if (!data) throw new Error('Authoritative node data missing');
        const visual = { x: item.x, y: item.y, width: item.width, height: item.height, shape: item.shape, theme_color: item.theme_color, container_theme: item.container_theme };
        item.setDBData(data); Object.assign(item, visual); item.currentBounding = null;
      } else if (op.type === 'NODE_GEOMETRY_SET' || op.type === 'NODE_SHAPE_SET') {
        const item = node(op.dbId);
        if (op.type === 'NODE_SHAPE_SET') item.setShape(op.shape as Shape);
        if (op.width !== undefined || op.height !== undefined) item.setSize(op.width ?? item.width, op.height ?? item.height);
        if (op.x !== undefined) item.x = op.x;
        if (op.y !== undefined) item.y = op.y;
      } else if (op.type === 'NODE_STYLE_SET') {
        const item = node(op.dbId);
        if (op.color) { item.setTheme(GetThemeByName(op.color)); item.container_theme = GetThemeByName(op.color); }
        if (op.textWidth !== undefined) item.textWidth = op.textWidth;
        if (op.dottedSpacing !== undefined) item.dottedSpacing = op.dottedSpacing;
      } else if (op.type === 'CONNECTION_REMOVE') {
        const connection = relations.get(op.relation);
        if (!connection) throw new Error('Connection outside captured Model');
        owner.connections = owner.connections.filter(item => item !== connection); relations.delete(op.relation);
      } else {
        let connection = relations.get(op.relation);
        if (op.type === 'CONNECTION_ADD') {
          if (connection) throw new Error('Connection already visible');
          const data = semantics.relations.get(op.relation);
          if (!data || data.type !== 'FLOW') throw new Error('Authoritative FLOW data missing');
          const from = node(op.sourceDbId), to = node(op.targetDbId);
          if (data.startNodeElementId !== from.dbData.elementId || data.endNodeElementId !== to.dbData.elementId) throw new Error('FLOW endpoints do not match');
          connection = new Connection(op.relation, from.id, to.id, 0, 0, 0, 0, from, to, data.type, '');
          connection.setRelation(data); owner.connections.push(connection); relations.set(op.relation, connection);
        }
        if (!connection) throw new Error('Connection outside captured Model');
        const { type, relation, ...style } = op;
        const allowed = fields['CONNECTION_STYLE_SET'];
        for (const [key, value] of Object.entries(style)) if (allowed.includes(key)) Object.assign(connection, { [key]: value });
      }
    }
    render?.();
  } catch (error) {
    for (const [object, descriptors] of undo) {
      for (const key of Object.keys(object)) if (!Object.prototype.hasOwnProperty.call(descriptors, key)) Reflect.deleteProperty(object, key);
      Object.defineProperties(object, descriptors);
    }
    owner.canvasItems = oldRoots; owner.connections = oldConnections;
    throw error;
  }
}
